import React, { useEffect, useState, useCallback } from 'react';
import '../../componentsStyling/ContentStyling.css';
import { DataGrid } from "@mui/x-data-grid";
import CreateReservationDialog from '../reservations/Crud/AddReservation.js';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { useInventoryAPIs } from '../../apis/InventoryAPIs.js';
import AddInventory from './AddInventory.js';
import SearchBar from '../general/SearchBar.js';
import { useInventorySearch } from './FilterInventory.js';
import CircularProgress from '@mui/material/CircularProgress';
import { handleInventoryCSVUpload } from '../../utils/csvImportHandler';
import useWindowSize  from '../../utils/windowSize.js';
import getColumnDef from './tables/table.js';
import DateFilter from './DateFilter';
import { useLocationAPIs } from '../../apis/LocationAPIs.js';
import { useStudentClassAPIs } from '../../apis/StudentClassAPIs.js';



function Inventory({ userRole }) {
    const { fetchInventory, deleteAsset, updateAsset, addAsset, fetchAvailableAssets } = useInventoryAPIs();
    const { getOrCreateLocation } = useLocationAPIs();
    const { getOrCreateStudentClass } = useStudentClassAPIs();
    const [inventory, setInventory] = useState([]);
    const [open, setOpen] = useState(false);
    // State for AddInventory dialog
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    // For row click
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [createReservationDialogOpen, setCreateReservationDialogOpen] = useState(false);
    const { filteredInventory, handleSearch } = useInventorySearch(inventory);
    const [loading, setLoading] = useState(true);
    const [importMessage, setImportMessage] = useState("");
    const [availabilityDate, setAvailabilityDate] = useState("");
    const [availabilityTime, setAvailabilityTime] = useState("");
    const [availabilityFilterActive, setAvailabilityFilterActive] = useState(false);

    const [originalInventory, setOriginalInventory] = useState([]);

    const loadInventory = useCallback(async () => {
        setLoading(true);
        try {
            const assetsWithTag = await fetchInventory();
            setInventory(assetsWithTag); // filtered inventory
            setOriginalInventory(assetsWithTag)
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [fetchInventory]);

    useEffect(() => {
        loadInventory();
    }, [loadInventory]);

    const loadMemoizedInventory = () => {
        setInventory(originalInventory);
        setAvailabilityFilterActive(false);
    };


    const onInventoryCSVUpload = (event) => {
        handleInventoryCSVUpload(event, loadInventory, alert, addAsset, getOrCreateLocation, getOrCreateStudentClass);
    };

    const onDelClick = (event, assetId) => {
        event.stopPropagation(); // Prevent row click event from firing
        if (window.confirm("Are you sure you want to delete?")) {
            deleteAsset(assetId).then((success) => {
                if (success) {
                    loadInventory();
                    setOpen(true); // Show the Snackbar on successful delete
                } else {
                    alert('Something went wrong!');
                }
            });
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleRowClick = (params) => {
        setSelectedRowData(params.row);
        setDialogOpen(true); // Closes the Create Reservation dialog
    };

    const handleDialogClose = () => {
        setDialogOpen(false); // Closes the Create Reservation dialog
        setSelectedRowData(null);
    };

    const handleAddDialogOpen = () => {
        setAddDialogOpen(true);
    };

    const handleAddDialogClose = () => {
        setAddDialogOpen(false);
    };

    const handleAddAsset = async (newAsset) => {
        const success = await addAsset(newAsset); // Call the addAsset API function
        if (success) {
            loadInventory(); // Reload inventory on successful add
            setAddDialogOpen(false); // Close dialog
            return true; // Indicate success
        }
        return false; // Indicate failure
    };

    const handleCreateReservationDialogOpen = () => {
        setCreateReservationDialogOpen(true);
    };

    const handleCreateReservationDialogClose = () => {
        setCreateReservationDialogOpen(false);
    };

    const handleOnSaveReservation = async (reservationData) => {
        handleCreateReservationDialogClose();
        handleDialogClose()
        loadInventory();
    };

    const handleCheckAvailability = async () => {
        if (!availabilityDate || !availabilityTime) {
            alert("Please enter both date and time.");
            return;
        }

        try {
            const availableAssets = await fetchAvailableAssets(availabilityDate, availabilityTime);
            setInventory(availableAssets);
            setAvailabilityFilterActive(true);
        } catch (err) {
            console.error("Failed to fetch available assets:", err);
            alert("Failed to check availability. Please try again.");
        }
    };

    const { width, height } = useWindowSize();
    const isSmallScreen = width < 900;
    
    // Calculate dynamic table height for desktop/laptop, accounting for components above
    // Header: 80px + 5px margin = 85px, Title container: ~80px, Search/Filter: ~100px, Padding: ~20px
    // Total: ~285px
    const tableHeight = !isSmallScreen 
        ? Math.max(400, height - 285) // Minimum 400px, subtract components above table
        : 600; // Keep fixed height for mobile

    const columnDefs = getColumnDef({
        userRole,
        loadInventory,
        onDelClick,
        isSmallScreen,
        updateAsset
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <div id="titleContainer">
                {(userRole === 'ROLE_ADMIN' || userRole === 'ROLE_STAFF') && (
                    <button className="add-button" onClick={handleAddDialogOpen} style={{paddingX: "16px"}}>
                        Add
                    </button>
                )}

                <h1 style={{margin:'5px'}}>Inventory</h1>
                
                {(userRole === 'ROLE_ADMIN' || userRole === 'ROLE_STAFF') && (
                    <button className="import-button">
                        <input
                            type="file"
                            accept=".csv"
                            id="csv-inventory-upload"
                            style={{ display: 'none' }}
                            onChange={onInventoryCSVUpload}
                        />

                        <label htmlFor="csv-inventory-upload" >
                            <span>Import CSV</span>
                        </label>
                    </button>
                )}
            </div>

            <div id="inventorySearchContainer">
                <SearchBar onSearch={handleSearch} />
                <DateFilter
                    date={availabilityDate}
                    time={availabilityTime}
                    onDateChange={setAvailabilityDate}
                    onTimeChange={setAvailabilityTime}
                    onFilter={handleCheckAvailability}
                    filterActive={availabilityFilterActive}
                    onReset={loadMemoizedInventory}
                />
            </div>


            <div className="table-container">
                <div className="dataGridContainer" style={{ width: '100%', maxWidth: 'min-content', height: `${tableHeight}px` }}>
                    <DataGrid
                        rows={filteredInventory}
                        columns={columnDefs}
                        getRowId={(row) => row.tag}
                        disableSelectionOnClick
                        rowHeight={80}
                        onRowClick={handleRowClick}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    tag: false,
                                    purchaseDate: false,
                                    location: false,
                                    type: false,
                                },
                            },
                        }}
                    />
                </div>
            </div>

            {/* Snackbar Component */}
            <Snackbar
                open={open}
                autoHideDuration={3000}
                onClose={handleClose}
                message="Asset deleted successfully"
            />
            <Snackbar
                open={!!importMessage}
                autoHideDuration={6000}
                onClose={() => setImportMessage("")}
                message={importMessage}
            />
            {/* Dialog to show row data */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>Row Details</DialogTitle>
                <DialogContent>
                    {selectedRowData && (
                        <div>
                            {Object.entries(selectedRowData).map(([key, value]) => (
                                <div key={key} style={{ margin: '10px 0' }}>
                                    <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>
                                    {Array.isArray(value) ? (
                                        value.length === 0 ? (
                                            <div>—</div>
                                        ) : (
                                            value.map((v, i) => (
                                                <div key={i} style={{ marginLeft: '10px' }}>
                                                    {typeof v === "object" && v !== null ? (
                                                        <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                                                            {Object.entries(v).map(([subKey, subVal]) => (
                                                                <li key={subKey}>
                                                                    <strong>{subKey}:</strong> {subVal?.toString() ?? ""}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <div>{v?.toString() ?? ""}</div>
                                                    )}
                                                </div>
                                            ))
                                        )
                                    ) : (
                                        <div>{value?.toString() ?? "—"}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {/* You bind the component opening functionality by passing the use state  */}
                    <CreateReservationDialog
                        open={createReservationDialogOpen}
                        onClose={handleCreateReservationDialogClose}
                        onSave={handleOnSaveReservation}
                        data={selectedRowData}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateReservationDialogOpen} sx={{ color: 'green' }}>
                        Create Reservation
                    </Button>
                    <Button onClick={handleDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>
            {/* Add Inventory Dialog */}
            <AddInventory
                open={addDialogOpen}
                onClose={handleAddDialogClose}
                onSave={handleAddAsset} // Pass handleAddAsset to onSave prop
            />
        </>
    );
}

export default Inventory;